import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, Link2, Linkedin, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { CTA, Reveal, useSeo } from "../components/Shared";
import { ARTICLES } from "../data/content";

const ShareBar = ({ article }) => {
  const url = typeof window !== "undefined" ? window.location.href : "";
  const copy = async () => {
    await navigator.clipboard.writeText(url);
    toast.success("Link copied to clipboard.");
  };
  return (
    <div className="share-bar" data-testid="article-share-bar">
      <span className="share-label">Share this insight</span>
      <a
        href={`https://wa.me/?text=${encodeURIComponent(`${article.title} ${url}`)}`}
        target="_blank" rel="noreferrer" className="share-btn whatsapp" aria-label="Share on WhatsApp"
        data-testid="share-whatsapp-button"
      >
        <MessageCircle size={16} /> WhatsApp
      </a>
      <a
        href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`}
        target="_blank" rel="noreferrer" className="share-btn linkedin" aria-label="Share on LinkedIn"
        data-testid="share-linkedin-button"
      >
        <Linkedin size={16} /> LinkedIn
      </a>
      <button onClick={copy} className="share-btn" aria-label="Copy link" data-testid="share-copy-button">
        <Link2 size={16} /> Copy link
      </button>
    </div>
  );
};

export default function Article() {
  const { slug } = useParams();
  const article = ARTICLES.find((a) => a.slug === slug) || ARTICLES[0];
  useSeo(`${article.title} | Dishan Insights`, article.excerpt);

  useEffect(() => {
    window.scrollTo(0, 0);
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Article",
      headline: article.title,
      description: article.excerpt,
      image: article.img,
      author: { "@type": "Organization", name: "Dishan International Group" },
      publisher: { "@type": "Organization", name: "Dishan International Group" },
    });
    document.head.appendChild(script);
    return () => script.remove();
  }, [article]);

  const related = ARTICLES.filter((a) => a.slug !== article.slug).slice(0, 2);

  return (
    <>
      <main>
        <article className="article-page" data-testid="article-page">
          <header className="article-header">
            <div className="container narrow">
              <Reveal>
                <Link to="/insights" className="text-link back-link" data-testid="article-back-link"><ArrowLeft size={14} /> All insights</Link>
                <span className="tag">{article.cat}</span>
                <h1>{article.title}</h1>
                <p className="article-meta">{article.date} · {article.read} · Dishan Knowledge Desk</p>
                <ShareBar article={article} />
              </Reveal>
            </div>
          </header>
          <Reveal className="container narrow article-cover">
            <img src={article.img} alt={article.title} />
          </Reveal>
          <div className="container narrow article-body" data-testid="article-body">
            <p className="article-lede">{article.excerpt}</p>
            {article.body.map((block, i) =>
              block.h ? <h2 key={`${article.slug}-h-${i}`}>{block.h}</h2> : <p key={`${article.slug}-p-${i}`}>{block.p}</p>
            )}
            <ShareBar article={article} />
          </div>
          <div className="container narrow related">
            <span className="eyebrow">Continue reading</span>
            <div className="related-grid">
              {related.map((a) => (
                <Link to={`/insights/${a.slug}`} key={a.slug} className="related-card" data-testid={`related-${a.slug}`}>
                  <span className="tag">{a.cat}</span>
                  <h3>{a.title}</h3>
                  <span className="insight-date">{a.read} <ArrowUpRight size={14} /></span>
                </Link>
              ))}
            </div>
          </div>
        </article>
        <CTA />
      </main>
    </>
  );
}
